import { useCallback, useEffect, useState } from 'react';

/**
 * How a climb's photograph is shown — **the reader's business, not the
 * setter's.**
 *
 * A wall is a busy picture and a climb is a handful of holds on it, so a
 * photograph of one usually wants help: the wall pushed back, and the climb's
 * own holds given their colour back. Which help it wants depends on the eye
 * looking at it (and on the gym's own walls), so it is a preference of the
 * person reading rather than a property of the climb — it used to be
 * `climbs.monochrome`, a switch in the gym panel, and a setter cannot answer
 * this for every phone the climb will be read on.
 *
 * Where it comes from, in order:
 *
 * 1. `?photo_effect=` in the URL — and from that moment it is remembered in a
 *    cookie, so one link settles it for every climb afterwards.
 *    `?photo_effect=default` forgets the choice again.
 * 2. the cookie a previous link left.
 * 3. `VITE_CLIMB_PHOTO_EFFECT` — what this deployment shows by default.
 * 4. `none`: the photograph as it was taken.
 *
 * The effects themselves:
 *
 * | effect | the wall | the climb's holds |
 * | --- | --- | --- |
 * | `none` | as taken | as taken |
 * | `monochrome` | black and white | their own colour |
 * | `darken` | dimmed | their own brightness, so they read as lit |
 * | `spotlight` | dimmed further | lit, and bleeding a little onto the wall |
 * | `blur` | out of focus | sharp |
 * | `fade` | washed out | colour, pushed up |
 *
 * **Only a climb made off a scanned wall wears one.** The effect pushes the wall
 * back and cuts the climb's holds out of the same picture, which needs their
 * outlines — and only a scan measured those. A climb circled by hand has circles
 * rather than shapes, so there is nothing to give back and the photograph is
 * shown as it was taken, whatever the link or the cookie says. The server says
 * which kind a climb is (`from_scan`); see `useClimbPhotoEffect`.
 */
export type ClimbPhotoEffect =
  | 'none'
  | 'monochrome'
  | 'darken'
  | 'spotlight'
  | 'blur'
  | 'fade';

export const CLIMB_PHOTO_EFFECTS: ClimbPhotoEffect[] = [
  'none',
  'monochrome',
  'darken',
  'spotlight',
  'blur',
  'fade',
];

export const DEFAULT_CLIMB_PHOTO_EFFECT: ClimbPhotoEffect = 'none';

/** What a link says to try an effect: `/gyms/x/boulders/y?photo_effect=darken`. */
export const CLIMB_PHOTO_EFFECT_PARAM = 'photo_effect';

/**
 * Where the answer is kept. A cookie rather than local storage because this is
 * the one viewer preference that a link hands out: it has to survive being
 * opened from a message, and a cookie is what the whole site — any host under
 * the domain, any tab — reads back without being asked twice.
 *
 * Technical storage in the sense of Art. 122: a user-interface preference the
 * visitor asked for by following the link. It is listed in the cookie policy's
 * "what we always store" table and is **not** behind the consent gate.
 */
export const CLIMB_PHOTO_EFFECT_COOKIE = 'divepeaks-photo-effect';

/** What the URL says to go back to whatever this deployment shows by default. */
export const CLIMB_PHOTO_EFFECT_RESET = 'default';

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/**
 * What an effect does, as the two CSS filters it is made of: one over the
 * photograph, one over the same photograph cut out to the climb's holds (see
 * `HoldsOverlay`). An effect that leaves `holds` out is a plain photograph with
 * nothing painted back — there is then no second layer at all.
 */
export interface ClimbPhotoPaint {
  /** Over the photograph itself. Left out, the picture is untouched. */
  wall?: string;
  /** Over the cut-out of the climb's own holds. Left out, there is no cut-out. */
  holds?: string;
  /**
   * How far the holds' light bleeds onto the wall around them, as a share of
   * the long edge of what is on screen — the one thing a flat filter cannot do,
   * and what makes a lit hold look lit rather than merely brighter.
   */
  bloom?: number;
}

/**
 * The numbers. `darken` borrows the two the panel's spotlight already uses
 * (`PhotoHoldCanvas`), which were set against a real wall: dark enough that a
 * lit hold is unmistakable across a room, light enough that the wall is still a
 * wall.
 *
 * **The API paints the same numbers** onto a shared link's preview picture
 * (`ClimbPhotoEffect::PAINT`, app/services/climb_photo_effect.rb): change one
 * here, change it there, and bump its VERSION.
 */
const PAINT: Record<ClimbPhotoEffect, ClimbPhotoPaint> = {
  none: {},
  monochrome: { wall: 'grayscale(1)', holds: 'saturate(1.5) brightness(1.06)' },
  darken: { wall: 'brightness(0.42)', holds: 'brightness(1.08) saturate(1.15)' },
  spotlight: {
    wall: 'brightness(0.32) saturate(0.85)',
    holds: 'brightness(1.18) saturate(1.3)',
    bloom: 0.01,
  },
  // The wall as depth of field rather than as light: it keeps its colour, which
  // is what a monochrome gym's own plastic needs.
  blur: { wall: 'blur(6px) saturate(0.9)', holds: 'saturate(1.1)' },
  // The wall washed out towards paper, the way a topo is drawn. Reads on a
  // phone held in the sun, where dimming the picture only makes it vanish.
  fade: { wall: 'contrast(0.6) brightness(1.25) saturate(0.35)', holds: 'saturate(1.45) contrast(1.1)' },
};

export function climbPhotoPaint(effect: ClimbPhotoEffect): ClimbPhotoPaint {
  return PAINT[effect] ?? PAINT.none;
}

/** Whether this effect needs the climb's holds painted back over the wall. */
export function climbPhotoRelightsHolds(effect: ClimbPhotoEffect): boolean {
  return !!climbPhotoPaint(effect).holds;
}

export function isClimbPhotoEffect(value: unknown): value is ClimbPhotoEffect {
  return typeof value === 'string' && (CLIMB_PHOTO_EFFECTS as string[]).includes(value);
}

/** What this deployment shows to someone who has never asked for anything. */
export function defaultClimbPhotoEffect(): ClimbPhotoEffect {
  const configured = import.meta.env.VITE_CLIMB_PHOTO_EFFECT;

  return isClimbPhotoEffect(configured) ? configured : DEFAULT_CLIMB_PHOTO_EFFECT;
}

/**
 * The effect a URL asks for: one of the names, the word that forgets the
 * choice, or nothing at all. Kept apart from reading the preference so that
 * *resolving* it stays a pure read and *remembering* it is an explicit write.
 */
export function climbPhotoEffectInUrl(
  search: string
): ClimbPhotoEffect | typeof CLIMB_PHOTO_EFFECT_RESET | null {
  let asked: string | null = null;
  try {
    asked = new URLSearchParams(search).get(CLIMB_PHOTO_EFFECT_PARAM);
  } catch {
    return null;
  }
  if (!asked) return null;
  if (asked === CLIMB_PHOTO_EFFECT_RESET) return CLIMB_PHOTO_EFFECT_RESET;

  return isClimbPhotoEffect(asked) ? asked : null;
}

function readCookie(name: string): string | null {
  try {
    const prefix = `${name}=`;
    const found = document.cookie
      .split('; ')
      .find((pair) => pair.startsWith(prefix));

    return found ? decodeURIComponent(found.slice(prefix.length)) : null;
  } catch {
    /* ignore a document with no cookies to give */
    return null;
  }
}

/** The remembered choice, or null where there is none to read. */
export function readClimbPhotoEffect(): ClimbPhotoEffect | null {
  const stored = readCookie(CLIMB_PHOTO_EFFECT_COOKIE);

  return isClimbPhotoEffect(stored) ? stored : null;
}

/** Remember an effect for every climb from here on — or, with null, forget it. */
export function writeClimbPhotoEffect(effect: ClimbPhotoEffect | null): void {
  try {
    document.cookie =
      effect === null
        ? `${CLIMB_PHOTO_EFFECT_COOKIE}=; path=/; max-age=0; samesite=lax`
        : `${CLIMB_PHOTO_EFFECT_COOKIE}=${effect}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; samesite=lax`;
  } catch {
    /* ignore a browser that refuses cookies: the URL still works for this visit */
  }
}

/**
 * Which effect to draw with, from the URL, the cookie and the deployment's own
 * default in that order. A pure read: following a link is what *remembers* it
 * (see `useClimbPhotoEffect`).
 */
export function resolveClimbPhotoEffect(search = windowSearch()): ClimbPhotoEffect {
  const asked = climbPhotoEffectInUrl(search);
  if (asked && asked !== CLIMB_PHOTO_EFFECT_RESET) return asked;
  // "default" means forget what I chose, so the cookie is skipped on the way
  // past rather than only cleared behind us.
  if (asked === CLIMB_PHOTO_EFFECT_RESET) return defaultClimbPhotoEffect();

  return readClimbPhotoEffect() ?? defaultClimbPhotoEffect();
}

function windowSearch(): string {
  try {
    return window.location.search;
  } catch {
    return '';
  }
}

/** The one thing about a climb that decides whether an effect applies to it. */
export interface ClimbPhotoSubject {
  from_scan?: boolean;
}

// Broadcast so every picture of the same climb on the page (the cover and the
// enlarged dialog) changes together when the reader cycles the effect.
const CLIMB_PHOTO_EFFECT_EVENT = 'climb-photo-effect-change';

/**
 * The link to share a climb by, carrying the effect it is being looked at with
 * (`?photo_effect=`): the preview a chat unfurls is drawn with it (og:image,
 * painted by the API — see `og-inject.ts`), and the page it opens shows it.
 *
 * Left off where there is nothing to hand on — a climb that cannot wear an
 * effect, or the effect this deployment shows anyway, which is what the preview
 * falls back to. Following such a link then leaves the reader's own choice
 * alone rather than overwriting it with the default.
 */
export function climbShareUrl(
  url: string,
  climb?: ClimbPhotoSubject | null,
  effect: ClimbPhotoEffect = resolveClimbPhotoEffect()
): string {
  try {
    const shared = new URL(url);
    shared.searchParams.delete(CLIMB_PHOTO_EFFECT_PARAM);
    if (climb?.from_scan && effect !== defaultClimbPhotoEffect()) {
      shared.searchParams.set(CLIMB_PHOTO_EFFECT_PARAM, effect);
    }

    return shared.toString();
  } catch {
    return url;
  }
}

/** The effect after this one, round the list and back to the bare photograph. */
export function nextClimbPhotoEffect(effect: ClimbPhotoEffect): ClimbPhotoEffect {
  return CLIMB_PHOTO_EFFECTS[(CLIMB_PHOTO_EFFECTS.indexOf(effect) + 1) % CLIMB_PHOTO_EFFECTS.length];
}

/** Take `?photo_effect=` off the address bar, leaving the rest of it alone. */
function forgetEffectInUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(CLIMB_PHOTO_EFFECT_PARAM)) return;
    url.searchParams.delete(CLIMB_PHOTO_EFFECT_PARAM);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    /* ignore a window with no history to rewrite */
  }
}

export interface ClimbPhotoEffectControl {
  /** The effect this climb is drawn with: `none` unless it was made off a scan. */
  effect: ClimbPhotoEffect;
  /** Whether the climb can wear one at all, i.e. whether offering to change it makes sense. */
  applies: boolean;
  /** Move on to the next effect, for every climb from here on. */
  cycle: () => void;
}

/**
 * The effect this reader is looking at *this climb* with: their choice for a
 * climb made off a scan, and `none` for any other — which is never given the
 * effect, and so never has its holds cut out of the picture.
 *
 * The choice itself is read once per mount: the URL cannot change under a
 * photograph without a navigation, and a link is remembered the moment it is
 * followed — **even on a climb the effect does not apply to**, so a link opened
 * on a hand-circled climb still sets the effect for the scanned ones after it.
 * After that only `cycle` changes it, remembering the new one in the same
 * cookie and telling the other pictures of the page. The climb is read on every
 * render, not once: a cover is reused as the reader swipes from one climb to
 * the next.
 */
export function useClimbPhotoEffectControl(climb?: ClimbPhotoSubject | null): ClimbPhotoEffectControl {
  const [chosen, setChosen] = useState(resolveClimbPhotoEffect);
  const applies = !!climb?.from_scan;

  useEffect(() => {
    const asked = climbPhotoEffectInUrl(windowSearch());
    if (asked === CLIMB_PHOTO_EFFECT_RESET) writeClimbPhotoEffect(null);
    else if (asked) writeClimbPhotoEffect(asked);
  }, []);

  useEffect(() => {
    const onChange = (e: Event) => setChosen((e as CustomEvent<ClimbPhotoEffect>).detail);
    window.addEventListener(CLIMB_PHOTO_EFFECT_EVENT, onChange);
    return () => window.removeEventListener(CLIMB_PHOTO_EFFECT_EVENT, onChange);
  }, []);

  const cycle = useCallback(() => {
    const next = nextClimbPhotoEffect(chosen);
    writeClimbPhotoEffect(next);
    // The link that set the effect is no longer what the reader is looking at,
    // and would win over the cookie the next time this page is read.
    forgetEffectInUrl();
    window.dispatchEvent(new CustomEvent(CLIMB_PHOTO_EFFECT_EVENT, { detail: next }));
  }, [chosen]);

  return { effect: applies ? chosen : DEFAULT_CLIMB_PHOTO_EFFECT, applies, cycle };
}

export function useClimbPhotoEffect(climb?: ClimbPhotoSubject | null): ClimbPhotoEffect {
  return useClimbPhotoEffectControl(climb).effect;
}
